/**
 * Store connection status utilities.
 *
 * A store is considered "connected" (i.e. ready for plan selection) when:
 *   - Shopify store: it has a valid `access_token` (backend sets `connected = !empty(access_token)`).
 *   - WordPress / WooCommerce store: its `connection_status` is "connected"
 *     (backend sets `connected = connection_status === 'connected'`).
 *
 * The backend already normalizes this into the boolean `connected` field on the
 * store payload, but we also keep the raw-field checks here so the frontend can
 * make the same decision even when the normalized flag is missing or stale
 * (e.g. right after a fresh login before stores are re-fetched).
 */

export interface StoreConnectionLike {
    shop?: string | null;
    connected?: boolean;
    platform_type?: string | null;
    /** Shopify access token presence indicates a connected Shopify store. */
    access_token?: string | null;
    /** WooCommerce connection status: 'pending' | 'connected' | 'disconnected'. */
    connection_status?: string | null;
    user_role?: string | null;
}

/**
 * Returns true when the given store is genuinely connected to its platform.
 *
 * - Shopify: requires a non-empty `access_token`.
 * - WooCommerce / WordPress: requires `connection_status === 'connected'`.
 * - Falls back to the normalized `connected` flag when raw fields are absent.
 */
export function isStoreConnected(store: StoreConnectionLike | null | undefined): boolean {
    if (!store) return false;

    // Temp stores are never considered connected.
    if (store.shop === 'temp-store') return false;

    const platform = (store.platform_type || '').toLowerCase();

    // WordPress / WooCommerce: connection_status must be 'connected'.
    if (platform === 'woocommerce' || platform === 'wordpress') {
        if (store.connection_status) {
            return store.connection_status === 'connected';
        }
        // connection_status not provided - fall back to normalized flag.
        return store.connected === true;
    }

    // Shopify (default platform): requires access_token.
    if (platform === 'shopify' || !platform) {
        // Prefer the explicit access_token check when available.
        if (store.access_token !== undefined) {
            return !!store.access_token;
        }
        // access_token not exposed to frontend - rely on normalized `connected`.
        return store.connected === true;
    }

    // Unknown platform - trust the normalized flag.
    return store.connected === true;
}

/**
 * Returns true when ANY store in the list is connected (Shopify with
 * access_token OR WooCommerce with connection_status === 'connected').
 */
export function hasAnyConnectedStore(stores: StoreConnectionLike[] | null | undefined): boolean {
    if (!stores || stores.length === 0) return false;
    return stores.some((s) => isStoreConnected(s));
}

/**
 * Returns true when the user owns at least one connected store.
 * Used by guards to decide whether Choose Plan should be reachable.
 */
export function hasConnectedOwnerStore(stores: StoreConnectionLike[] | null | undefined): boolean {
    if (!stores || stores.length === 0) return false;
    return stores.some(
        (s) => isStoreConnected(s) && (s.user_role === 'owner' || !s.user_role),
    );
}
